import React, { useEffect, useState } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import {
 Badge,
 Button,
 Card,
 Col,
 Dropdown,
 Form,
 Modal,
 Row,
 Spinner,
 Table,
} from "react-bootstrap";
import { useSelector } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import SignatureModal from "../Common/SignatureModal";
import PageTitle from "../../layouts/PageTitle";
import AppPagination from "../Common/AppPagination";
import api from "../../../api";
import swal from "sweetalert";
import PortalMenu from "../Common/PortalMenu";
import VerifiedCopyActions from "../Common/VerifiedCopyActions";

const VendorWorkOrderList = () => {
 const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectFromUrl = searchParams.get("project") || "";
  const workOrderFromUrl = searchParams.get("workOrder") || "";
 const user = useSelector((state) => state.auth?.userDetails);
 const [orders, setOrders] = useState([]);
 const [selectedWO, setSelectedWO] = useState(null);
 const [showModal, setShowModal] = useState(false);
 const [loading, setLoading] = useState(false);
 const [previewingPdf, setPreviewingPdf] = useState(false);
 const [showSignModal, setShowSignModal] = useState(false);
 const [selectedWOForStatus, setSelectedWOForStatus] = useState(null);
 const [statusToUpdate, setStatusToUpdate] = useState("");
 const [filters, setFilters] = usePersistentFilters("vendor_work_orders_filters", {
 search: "",
 status: "",
 page: 1,
 limit: 10,
 });
 const [pagination, setPagination] = useState({
 currentPage: 1,
 totalPages: 1,
 totalCount: 0,
 hasPrevPage: false,
 hasNextPage: false,
 });

 const fetchVendorWorkOrders = async () => {
 try {
 setLoading(true);
 const queryParams = new URLSearchParams();
 if (filters.search) queryParams.append("search", filters.search);
    if (projectFromUrl) queryParams.append("project", projectFromUrl);
    if (workOrderFromUrl) queryParams.append("workOrder", workOrderFromUrl);
 if (filters.status) queryParams.append("status", filters.status);
 queryParams.append("page", filters.page);
 queryParams.append("limit", filters.limit);

 const response = await api.get(`/vendor-work-orders?${queryParams.toString()}`);
 if (response.data.success) {
 const validOrders = (response.data.data || []).filter(
  (wo) => wo.project && (wo.project._id || wo.project.projectName)
 );
 setOrders(validOrders);
 if (response.data.pagination) {
 setPagination(response.data.pagination);
 }
 }
 } catch (error) {
 console.error("Error fetching vendor work orders:", error);
 swal("Error", "Failed to fetch vendor work orders", "error");
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
 fetchVendorWorkOrders();
 }, [filters.page, filters.limit, filters.status]);

 const handleSearch = () => {
 setFilters((prev) => ({ ...prev, page: 1 }));
 fetchVendorWorkOrders();
 };

 const handlePageChange = (page) => {
 if (page < 1 || page > pagination.totalPages) return;
 setFilters((prev) => ({ ...prev, page }));
 };

 const statusVariant = (status) => {
 switch (status) {
 case "Pending":
 case "Draft":
 return "warning";
 case "Approved":
 return "success";
 case "Rejected":
 return "danger";
 case "Issued":
 case "Accepted":
 return "primary";
 case "In Progress":
 return "info";
 case "Completed":
 case "Closed":
 return "success";
 default:
 return "secondary";
 }
 };

 const handleUpdateStatus = async (id, status) => {
 setSelectedWOForStatus(id);
 setStatusToUpdate(status);

 if (status === "Approved") {
 setShowSignModal(true);
 return;
 }

 const confirm = await swal({
 title: "Are you sure?",
 text: `Do you want to ${status.toLowerCase()} this vendor work order?`,
 icon: "warning",
 buttons: ["Cancel", "Yes"],
 dangerMode: status === "Rejected",
 });

 if (!confirm) return;

 try {
 const response = await api.patch(`/vendor-work-orders/${id}/status`, { status });
 if (response.data.success) {
 swal("Success", `Vendor work order ${status.toLowerCase()} successfully`, "success");
 fetchVendorWorkOrders();
 }
 } catch (error) {
 swal(
 "Error",
 error.response?.data?.message || "Failed to update vendor work order status",
 "error"
 );
 }
 };

 const confirmStatusUpdate = async (id, status, signFile = null) => {
 try {
 const result = await swal({
 title: "Are you sure?",
 text: `Do you want to ${status.toLowerCase()} this vendor work order?`,
 icon: "warning",
 buttons: ["Cancel", "Yes"],
 });

 if (!result) return;

 const payload = { status };
 if (signFile) {
 const reader = new FileReader();
 reader.readAsDataURL(signFile);
 reader.onloadend = async () => {
 try {
 payload.sign = reader.result;
 payload.field = "verifiedBySign";
 const response = await api.patch(`/vendor-work-orders/${id}/status`, payload);
 if (response.data.success) {
 swal("Success", `Vendor work order ${status.toLowerCase()} successfully`, "success");
 fetchVendorWorkOrders();
 setShowSignModal(false);
 }
 } catch (patchError) {
 swal(
 "Error",
 patchError.response?.data?.message || "Failed to update vendor work order status",
 "error"
 );
 }
 };
 } else {
 const response = await api.patch(`/vendor-work-orders/${id}/status`, payload);
 if (response.data.success) {
 swal("Success", `Vendor work order ${status.toLowerCase()} successfully`, "success");
 fetchVendorWorkOrders();
 setShowSignModal(false);
 }
 }
 } catch (error) {
 swal(
 "Error",
 error.response?.data?.message || "Failed to update vendor work order status",
 "error"
 );
 }
 };

 const handleView = async (id) => {
 try {
 const response = await api.get(`/vendor-work-orders/${id}`);
 if (response.data.success) {
 setSelectedWO(response.data.data);
 setShowModal(true);
 }
 } catch (error) {
 swal(
 "Error",
 error.response?.data?.message || "Failed to load vendor work order details",
 "error"
 );
 }
 };

 const handlePreviewPDF = async (id) => {
 try {
 setPreviewingPdf(true);
 const res = await api.get(`/vendor-work-orders/${id}/pdf`, {
 responseType: "blob",
 });
 const fileURL = window.URL.createObjectURL(
 new Blob([res.data], { type: "application/pdf" })
 );
 window.open(fileURL, "_blank");
 } catch (error) {
 swal("Error", "Failed to preview PDF", "error");
 } finally {
 setPreviewingPdf(false);
 }
 };

 const handleDownloadPDF = async (id) => {
 try {
 const res = await api.get(`/vendor-work-orders/${id}/pdf`, {
 responseType: "blob",
 });
 const url = window.URL.createObjectURL(new Blob([res.data]));
 const link = document.createElement("a");
 link.href = url;
 link.download = `VendorWO-${id}.pdf`;
 link.click();
 } catch (error) {
 swal("Error", "Failed to download PDF", "error");
 }
 };

 const handleDelete = async (id) => {
 const confirm = await swal({
 title: "Are you sure?",
 text: "This vendor work order will be permanently deleted.",
 icon: "warning",
 buttons: ["Cancel", "Delete"],
 dangerMode: true,
 });
 if (!confirm) return;
 try {
 const res = await api.delete(`/vendor-work-orders/${id}`);
 if (res.data.success) {
 swal("Deleted!", res.data.message || "Vendor Work Order deleted", "success");
 fetchVendorWorkOrders();
 }
 } catch (error) {
 swal(
 "Cannot Delete",
 error.response?.data?.message || "Failed to delete vendor work order",
 "warning"
 );
 }
 };

 const formatDate = (dateString) => {
 if (!dateString) return "-";
 try {
 return new Date(dateString).toLocaleDateString("en-IN", {
 year: "numeric",
 month: "short",
 day: "numeric",
 });
 } catch {
 return "-";
 }
 };

 const formatCurrency = (amount) => {
 if (amount === null || amount === undefined) return "Rs. 0";
 try {
 return `Rs. ${Number(amount).toLocaleString("en-IN")}`;
 } catch {
 return `Rs. ${amount}`;
 }
 };

 const currentRole = String(
 user?.role?.name || user?.roleName || user?.role || ""
 ).toLowerCase();
 const canManage =
 currentRole === "admin" ||
 Boolean(
 user?.permissions?.includes("create:vendorwo") ||
 user?.permissions?.includes("edit:vendorwo") ||
 user?.permissions?.includes("approve:vendorwo") ||
 user?.permissions?.includes("reject:vendorwo") ||
 user?.permissions?.includes("delete:vendorwo") ||
 user?.permissionNames?.includes("create:vendorwo") ||
 user?.permissionNames?.includes("edit:vendorwo") ||
 user?.permissionNames?.includes("approve:vendorwo") ||
 user?.permissionNames?.includes("reject:vendorwo") ||
 user?.permissionNames?.includes("delete:vendorwo")
 );
 const canCreate =
 currentRole === "admin" ||
 Boolean(
 user?.permissions?.includes("create:vendorwo") ||
 user?.permissionNames?.includes("create:vendorwo")
 );

 const svgMenu = (
 <svg width="20px" height="20px" viewBox="0 0 24 24" aria-hidden="true">
 <circle fill="#000000" cx="12" cy="5" r="2"></circle>
 <circle fill="#000000" cx="12" cy="12" r="2"></circle>
 <circle fill="#000000" cx="12" cy="19" r="2"></circle>
 </svg>
 );

 return (
 <>
 <PageTitle activeMenu="Vendor Work Orders" motherMenu="Commercial" />

 <Card>
 <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
 <Card.Title>Vendor Work Orders</Card.Title>
 <div className="d-flex gap-2">
 <Button
 variant="secondary"
 onClick={() => navigate("/purchase-orders-by-project")}
 >
 Back
 </Button>
 <Button
 variant="outline-primary"
 onClick={() => navigate("/vendor-work-order-billing")}
 >
 Vendor Billing
 </Button>
 {canCreate && (
 <Button
 variant="primary"
 onClick={() => navigate(projectFromUrl || workOrderFromUrl ? `/vendor-work-orders/add?${projectFromUrl ? `project=${projectFromUrl}&` : ""}${workOrderFromUrl ? `workOrder=${workOrderFromUrl}` : ""}` : "/vendor-work-orders/add")}
 >
 Add Vendor Work Order
 </Button>
 )}
 </div>
 </Card.Header>

 <Card.Body>
 <Row className="g-2 mb-3 align-items-stretch">
 <Col md={4}>
 <Form.Control
 className="h-100"
 style={{ minHeight: "48px" }}
 placeholder="Search WO number, title..."
 value={filters.search}
 onChange={(e) =>
 setFilters((prev) => ({ ...prev, search: e.target.value }))
 }
 onKeyDown={(e) => e.key === "Enter" && handleSearch()}
 />
 </Col>
 <Col md={3}>
 <Form.Select
 className="h-100"
 style={{ minHeight: "48px" }}
 value={filters.status}
 onChange={(e) =>
 setFilters((prev) => ({
 ...prev,
 status: e.target.value,
 page: 1,
 }))
 }
 >
 <option value="">All Status</option>
 <option value="Pending">Pending</option>
 <option value="Approved">Approved</option>
 <option value="Rejected">Rejected</option>
 <option value="Draft">Draft</option>
 <option value="Issued">Issued</option>
 <option value="Accepted">Accepted</option>
 <option value="In Progress">In Progress</option>
 <option value="Completed">Completed</option>
 <option value="Closed">Closed</option>
 </Form.Select>
 </Col>
 <Col md="auto" className="d-flex">
 <Button
 variant="outline-primary"
 className="h-100 px-4"
 style={{ minHeight: "48px" }}
 onClick={handleSearch}
 >
 Search
 </Button>
 </Col>
 </Row>

 {loading ? (
 <div className="text-center py-5">
 <Spinner />
 </div>
 ) : (
 <>
 <Table responsive bordered hover className="align-middle text-center">
 <thead>
 <tr>
 <th>WO Number</th>
 <th>Title</th>
 <th>Vendor</th>
 <th>Items</th>
 <th>Status</th>
 <th>Total</th>
 <th>Issue Date</th>
 <th className="text-center">Actions</th>
 </tr>
 </thead>
 <tbody>
 {orders.length === 0 ? (
 <tr>
 <td colSpan="8" className="text-center text-muted py-4">
 No vendor work orders found
 </td>
 </tr>
 ) : (
 orders.map((wo) => (
 <tr key={wo._id}>
 <td className="align-middle">{wo.woNumber || "-"}</td>
 <td className="align-middle">{wo.title || "-"}</td>
 <td className="align-middle">{wo.vendor?.name || wo.vendor?.companyName || "-"}</td>
 <td className="align-middle">{wo.items?.length || 0}</td>
 <td className="align-middle">
 <div className="d-flex justify-content-center">
 <Badge bg={statusVariant(wo.status)}>{wo.status}</Badge>
 </div>
 </td>
 <td className="align-middle">Rs. {Number(wo.totalAmount || 0).toLocaleString()}</td>
 <td className="align-middle">{wo.issueDate ? formatDate(wo.issueDate) : "-"}</td>
 <td className="align-middle overflow-visible">
 <div className="d-flex justify-content-end align-items-center gap-2 flex-nowrap">
 <VerifiedCopyActions
 id={wo._id}
 type="vendor-work-orders"
 moduleKey="vendorwo"
 hasVerifiedCopy={Boolean(wo.verifiedCopy?.filePath || wo.verifiedCopy?.fileUrl)}
 verifiedCopy={wo.verifiedCopy}
 onUploadSuccess={fetchVendorWorkOrders}
 canUpload={canManage}
 />
 <Dropdown>
 <Dropdown.Toggle
 variant=""
 className="btn-link i-false p-0 cursor-pointer ms-auto"
 as="div"
 title="Click for more actions"
 >
 {svgMenu}
 </Dropdown.Toggle>
 <Dropdown.Menu align="end" as={PortalMenu}>
 <Dropdown.Item onClick={() => handleView(wo._id)}>
 <i className="fas fa-eye me-2 text-primary"></i>
 View Details
 </Dropdown.Item>
 <Dropdown.Item
 onClick={() => handlePreviewPDF(wo._id)}
 disabled={previewingPdf}
 >
 <i className="fas fa-eye me-2 text-info"></i>
 Preview PDF
 </Dropdown.Item>
 <Dropdown.Item onClick={() => handleDownloadPDF(wo._id)}>
 <i className="fas fa-file-pdf me-2 text-danger"></i>
 Download PDF
 </Dropdown.Item>
 {canManage && wo.status === "Approved" && (
 <Dropdown.Item
 onClick={() =>
 navigate(
 `/vendor-work-order-billing/add?vendorWorkOrder=${wo._id}`
 )
 }
 >
 <i className="fas fa-file-invoice me-2 text-success"></i>
 Create Vendor Bill
 </Dropdown.Item>
 )}
 {canManage && wo.status === "Approved" && (
 <Dropdown.Item
 onClick={() =>
 navigate(
 `/vendor-jmr/add?vendorWorkOrder=${wo._id}`
 )
 }
 >
 <i className="fas fa-ruler-combined me-2 text-info"></i>
 Create JMR
 </Dropdown.Item>
 )}
 {canManage && ["Pending", "Draft"].includes(wo.status) && (
 <>
 <Dropdown.Divider />
 <Dropdown.Item onClick={() => handleUpdateStatus(wo._id, "Approved")}>
 <i className="fas fa-check me-2 text-success"></i>
 Approve
 </Dropdown.Item>
 <Dropdown.Item onClick={() => handleUpdateStatus(wo._id, "Rejected")}>
 <i className="fas fa-times me-2 text-danger"></i>
 Reject
 </Dropdown.Item>
 </>
 )}
 {canManage && (
 <>
 <Dropdown.Item
 as={Link}
 to={`/vendor-work-orders/edit/${wo._id}?${projectFromUrl ? `project=${projectFromUrl}&` : (wo.project?._id || wo.project ? `project=${wo.project._id || wo.project}&` : "")}${workOrderFromUrl ? `workOrder=${workOrderFromUrl}` : ""}`}
 >
 <i className="fas fa-edit me-2 text-warning"></i>
 Edit
 </Dropdown.Item>
 <Dropdown.Divider />
 <Dropdown.Item
 className="text-danger"
 onClick={() => handleDelete(wo._id)}
 >
 <i className="fas fa-trash me-2"></i>
 Delete
 </Dropdown.Item>
 </>
 )}
 </Dropdown.Menu>
 </Dropdown>
 </div>
 </td>
 </tr>
 ))
 )}
 </tbody>
 </Table>

 <AppPagination
 currentPage={pagination.currentPage}
 totalPages={pagination.totalPages}
 totalCount={pagination.totalCount}
 itemsLength={orders.length}
 limit={filters.limit}
 onPageChange={handlePageChange}
 />
 </>
 )}
 </Card.Body>
 </Card>

 {/* VIEW MODAL */}
 <Modal
 show={showModal}
 onHide={() => {
 setShowModal(false);
 setSelectedWO(null);
 }}
 size="lg"
 centered
 >
 <Modal.Header closeButton>
 <Modal.Title>
 Vendor WO: {selectedWO?.woNumber}
 </Modal.Title>
 </Modal.Header>

 <Modal.Body>
 {!selectedWO ? (
 <div className="text-center py-5">
 <div className="spinner-border text-primary"></div>
 </div>
 ) : (
 <>
 <Row className="mb-3">
 <Col md={6}>
 <p>
 <strong>WO Number:</strong>{" "}
 <span className="text-primary">{selectedWO.woNumber || "-"}</span>
 </p>
 <p>
 <strong>Title:</strong> {selectedWO.title || "-"}
 </p>
 <p>
 <strong>Vendor:</strong>{" "}
 {selectedWO.vendor?.name ||
 selectedWO.vendor?.companyName ||
 "N/A"}
 </p>
 <p>
 <strong>Status: </strong>
 <Badge
 bg={statusVariant(selectedWO.status)}
 className="rounded-pill"
 >
 {selectedWO.status}
 </Badge>
 </p>
 </Col>
 <Col md={6}>
 <p>
 <strong>Issue Date:</strong>{" "}
 {formatDate(selectedWO.issueDate || selectedWO.createdAt)}
 </p>
 <p>
 <strong>Expected Completion:</strong>{" "}
 {formatDate(selectedWO.expectedCompletionDate)}
 </p>
 {selectedWO.description && (
 <p>
 <strong>Description:</strong> {selectedWO.description}
 </p>
 )}
 </Col>
 </Row>

 <hr />

 <Row>
 <Col md={6}>
 <strong>Total Amount:</strong>{" "}
 <span className="text-success fs-5">
 {formatCurrency(selectedWO.totalAmount)}
 </span>
 </Col>
 </Row>

 {selectedWO.items?.length > 0 && (
 <>
 <hr />
 <h6>Scope of Work / Items</h6>
 <Table bordered size="sm">
 <thead className="table-light">
 <tr>
 <th>#</th>
 <th>Item</th>
 <th>Description</th>
 <th>Qty</th>
 <th>Unit</th>
 <th>Rate</th>
 <th>Amount</th>
 </tr>
 </thead>
 <tbody>
 {selectedWO.items.map((item, index) => (
 <tr key={index}>
 <td>{index + 1}</td>
 <td>{item.itemName}</td>
 <td>{item.description || "-"}</td>
 <td>{item.quantity}</td>
 <td>{item.unit}</td>
 <td>{formatCurrency(item.rate)}</td>
 <td>{formatCurrency(item.amount)}</td>
 </tr>
 ))}
 </tbody>
 </Table>
 </>
 )}

 {selectedWO.termsAndConditions && (
 <>
 <hr />
 <h6>Terms &amp; Conditions</h6>
 <div
 dangerouslySetInnerHTML={{ __html: selectedWO.termsAndConditions }}
 className="text-muted"
 />
 </>
 )}
 </>
 )}
 </Modal.Body>

 <Modal.Footer>
 <Button variant="secondary" onClick={() => setShowModal(false)}>
 Close
 </Button>
 {selectedWO && canManage && (
 <Button
 as={Link}
 to={`/vendor-work-orders/edit/${selectedWO._id}?${projectFromUrl ? `project=${projectFromUrl}&` : (selectedWO.project?._id || selectedWO.project ? `project=${selectedWO.project._id || selectedWO.project}&` : "")}${workOrderFromUrl ? `workOrder=${workOrderFromUrl}` : ""}`}
 variant="primary"
 onClick={() => setShowModal(false)}
 >
 <i className="fas fa-edit me-2"></i>Edit
 </Button>
 )}
 </Modal.Footer>
 </Modal>

 <SignatureModal
 show={showSignModal}
 onHide={() => setShowSignModal(false)}
 onSave={(file) => confirmStatusUpdate(selectedWOForStatus, statusToUpdate, file)}
 title="Verified By - Approval Signature"
 />

 {previewingPdf && (
 <div
 className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
 style={{ background: "rgba(0,0,0,0.4)", zIndex: 9999 }}
 >
 <div className="bg-white p-4 rounded shadow text-center">
 <div className="spinner-border text-primary mb-3"></div>
 <h6 className="mb-0">Preparing Vendor Work Order PDF...</h6>
 </div>
 </div>
 )}
 </>
 );
};

export default VendorWorkOrderList;
